import { OverlayBackground } from '@shared/overlay/overlay.type';
import { MiniTerminalActuationType, MiniTerminalMountingType } from '../../service/mini-terminal-article-info-def';
import { WizardModel } from '../wizard-model';
import { ArticleColorOption, ConfigurationTemplate, QuantitySelectionOption, TerminalColorSelectionOption, TerminalOption, TileSelectionOption } from '../wizard-model.interface';
import { Log } from '@shared/debug/log';
import { OpenLabelEditorHelper } from '@project/components/label/label-editor/service/open-label-editor.helper';
import { OverlayService } from '@shared/overlay/overlay.service';
import { RouteNavigationService } from '@services/route-navigation.service';
import { ConfigurationCreationService } from '../../service/configuration-creation.service';
import { MiniTerminalBlocksColorVariants, MiniTerminalBlocksProjectCreationService } from '../../service/mini-terminal-blocks-project-creation.service';
import { ArticleColorVariantsInfoVO, ArticleColorVariantVO } from '@article/model/vo/article-color-variants-info.vo';
import { Injectable } from '@angular/core';

@Injectable()
export class MiniRailModel extends WizardModel {
  public readonly type = "MINI_TERMINAL";

  private _selectedOptions: {[option: string]: string | string[] | number} = {};
  private _colorVariants?: MiniTerminalBlocksColorVariants;

  constructor(
    private routeNavigationService: RouteNavigationService,
    private miniTerminalBlocksProjectCreationService: MiniTerminalBlocksProjectCreationService,
    private overlayService: OverlayService,
    private configurationCreationService: ConfigurationCreationService,
    private openLabelEditorHelper: OpenLabelEditorHelper
  ) {
    super();
  }

  public selectValue(step: number, value: any): void {
    const currentStepConfig = this.config.steps[step];
    if (step < 0 || !currentStepConfig) {
      return;
    }
    const currentStepTitle = currentStepConfig.id;
    this._selectedOptions[currentStepTitle] = value;
  }

  public async create(): Promise<void> {
    if (this.creationStarted) {
      return;
    }

    this.creationStarted = true;

    await this.overlayService.doWithOverlay(async () => {
      await this.configurationCreationService.prepareNewConfiguration(this.metaData);
      await this.miniTerminalBlocksProjectCreationService.createProject(this._selectedOptions['colors'] as string[]);

      try {
        await this.routeNavigationService.toConfigurator();
        await this.openLabelEditorHelper.showLabelEditorForMiniTerminalBlocksProject();
      } catch (error) {
        Log.error('Could not route to configurator: ', error);
        return;
      }
      this.creationStarted = false;
    },
    OverlayBackground.color);
  }

  protected createConfig(): ConfigurationTemplate {
    return {
      id: "create-project-mini-terminal",
      type: 'assistant',
      imageUrl: "ng-assets/img/projecttypes/mini-terminal-blocks.webp",
      label: "configurationCreation.type.mini_terminal_blocks",
      steps: [
        {
          type: "meta",
          id: "meta",
          label: "configurationCreation.progress.configuration_data",
        },
        {
          type: "tileSelection",
          id: "rating",
          label: "createConfigurationAssistant.progress.rating.label",
          title: "createConfigurationAssistant.question.rating_data",
          info: "createMiniTerminalBlocksDialog.option.size.stripped_conductors_info",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMiniTerminalBlocksDialog.option.size.1mm",
              id: "size-1mm",
              value: 1,
              imageUrl: "ng-assets/img/mini-terminal-blocks/option_1_1.png",
              size: "L"
            },
            {
              label: "createMiniTerminalBlocksDialog.option.size.2_5mm",
              id: "size-2_5mm",
              value: 2.5,
              imageUrl: "ng-assets/img/mini-terminal-blocks/option_1_2.png",
              size: "L"
            }
          ]
        },
        {
          type: "tileSelection",
          id: "mounting",
          label: "createConfigurationAssistant.progress.mount.label",
          title: "createConfigurationAssistant.question.mounting_type",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMiniTerminalBlocksDialog.option.mounting_type.TS15",
              id: "mounting-type-ts15",
              value: MiniTerminalMountingType.TS15,
              imageUrl: "ng-assets/img/mini-terminal-blocks/option_2_2_TS15.png",
              size: "M"
            },
            {
              label: "createMiniTerminalBlocksDialog.option.mounting_type.snap_foot",
              id: "mounting-type-snap_foot",
              value: MiniTerminalMountingType.SNAP_FOOT,
              imageUrl: "ng-assets/img/mini-terminal-blocks/option_2_3_snap_foot.png",
              size: "M"
            },
            {
              label: "createMiniTerminalBlocksDialog.option.mounting_type.flange",
              id: "mounting-type-flange",
              value: MiniTerminalMountingType.FLANGE,
              imageUrl: "ng-assets/img/mini-terminal-blocks/option_2_4_flange.png",
              size: "M"
            }
          ]
        },
        {
          type: "tileSelection",
          id: "actuation",
          label: "createConfigurationAssistant.progress.actuation_type.label",
          title: "createConfigurationAssistant.question.actuation_type",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMiniTerminalBlocksDialog.option.actuation_type.push_button",
              id: "actuation-type-push_button",
              value: MiniTerminalActuationType.PUSH_BUTTON,
              imageUrl: "ng-assets/img/mini-terminal-blocks/option_3_1push_button.png",
              size: "M"
            },
            {
              label: "createMiniTerminalBlocksDialog.option.actuation_type.push_in",
              id: "actuation-type-push_in",
              value: MiniTerminalActuationType.PUSH_IN,
              imageUrl: "ng-assets/img/mini-terminal-blocks/option_3_2_operating_slots.png",
              size: "M"
            }
          ]
        },
        {
          type: "quantitySelection",
          id: "amount",
          label: "createConfigurationAssistant.progress.amount.label",
          info: "createConfigurationAssistant.question.potential_count.max_more_on_request",
          title: "createConfigurationAssistant.question.potential_count",
          getOptions: async (): Promise<QuantitySelectionOption[]> => this.getUsedOptions(),
        },
        {
          type: "terminalColorSelection",
          id: "colors",
          label: "createConfigurationAssistant.progress.colors.label",
          title: "createConfigurationAssistant.question.colors",
          getOptions: async (): Promise<TerminalColorSelectionOption> => this.createColorOptions(),
        }
      ]
    };
  }

  private getUsedOptions(): QuantitySelectionOption[] {
    const selectedType = this._selectedOptions["mounting"];
    switch (selectedType) {
      case MiniTerminalMountingType.TS15:
      case MiniTerminalMountingType.TS35:
      case MiniTerminalMountingType.SNAP_FOOT:
        return [ {
          max: 36,
          min: 2,
        } ];
      case MiniTerminalMountingType.FLANGE:
        return [ {
          max: 12,
          min: 2,
        } ];
      default:
        return [];
    }
  }

  private async createColorOptions(): Promise<TerminalColorSelectionOption> {
    try {
      await this.overlayService.doWithOverlay(async () => {
        try {
          this._colorVariants = await this.miniTerminalBlocksProjectCreationService.getTerminalColorVariants({
            size: this._selectedOptions['rating'] as number,
            mountingType: this._selectedOptions['mounting'] as MiniTerminalMountingType,
            actuationType: this._selectedOptions['actuation'] as MiniTerminalActuationType,
            potentialCount: this._selectedOptions['amount'] as number
          });
        } catch (error) {
          Log.warn('Could not load color variants', error?.toString());
        }
      }, OverlayBackground.color);
    } catch (error) {
      Log.warn('Could not load color variants in overlay', error?.toString());
    }

    return { colors: this.getColors(), terminals: this.getTerminals() };
  }

  private getColors(): ArticleColorOption[] {
    const colorOptions: ArticleColorOption[] = [];

    if (!this._colorVariants) {
      return colorOptions;
    }

    for (const colorVariantInfo of this._colorVariants.variants) {
      for (const colorVariant of colorVariantInfo.variants) {
        const id = colorVariant.colorId;
        if (!colorOptions.some((option) => option.colorId === id)) {
          colorOptions.push({
            id: `color_${id}`,
            articleNo: colorVariant.articleNo,
            colorId: id,
            color: colorVariant.color,
          });
        }
      }
    }
    return colorOptions;
  }

  private getTerminals(): TerminalOption[] {
    const terminals: TerminalOption[] = [];
    if (!this._colorVariants) {
      return terminals;
    }

    for (const articleNo of this._colorVariants.terminalArticleNumbers) {
      const colorVariantsInfo = this.getVariantsInfoByArticleNo(articleNo);
      const colorVariant = colorVariantsInfo?.variants.find((variant: ArticleColorVariantVO) => variant.articleNo === articleNo);
      if (!colorVariantsInfo || !colorVariant) {
        continue;
      }
      const terminal: TerminalOption = {
        colorId: colorVariant.colorId,
        color: colorVariant.color,
        articleNo: colorVariant.articleNo,
        variants: colorVariantsInfo.variants,
      };
      terminals.push(terminal);
    }

    return terminals;
  }

  private getVariantsInfoByArticleNo(articleNo: string): ArticleColorVariantsInfoVO | undefined {
    if (!this._colorVariants || this._colorVariants.variants.length == 0) {
      return;
    }
    return this._colorVariants.variants.find((colorVariantInfo: ArticleColorVariantsInfoVO) =>
      colorVariantInfo.variants.some((colorVariant: ArticleColorVariantVO) => colorVariant.articleNo === articleNo)
    );
  }
}
